import * as React from 'react';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import type {
    ActivityCreateInput,
    ActivityUpdateInput,
    GetActivityForModal_activity as ActivityForModal,
    ActivityDeleteInput,
} from 'nutshell-graphql-types';

import {usePoll} from '../../util/hooks/use-poll';

import {useGetTeams} from '../core/graphql/hooks/use-get-teams';
import {useGetSessionUser} from '../core/use-get-session-user';
import {refetchForEvent} from '../lead/refetch-helpers';

import {ActivityModalContextProvider} from './activity-modal-context';
import {
    useUpdateActivityForModal,
    useGetActivityForModal,
    useGetPotentialActivityEntities,
    useCreateActivityForModal,
    useDeleteActivity,
    useGetActivityTranscriptAndSummary,
    useGetCalendarSyncForActivityModal,
    useGetActivitySyncStatus,
} from './graphql/hooks';
import {getRelatedEntityIds, refreshActivityState, isModalCreating} from './helpers';
import type {UpdateLocation, ModalVariant} from './types';

import {ActivityModal} from '.';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    activityId: string;
    relatedEntityId: string;
    variant: ModalVariant;
    history: RouterHistory;
};

export function ActivityModalRoot(props: Props) {
    const {variant} = props;
    const isCreating = isModalCreating(variant);

    const [createdActivity, setCreatedActivity] = React.useState<
        ActivityForModal | null | undefined
    >(null);
    const [lastUpdateLocation, setLastUpdateLocation] = React.useState<UpdateLocation | null>(null);
    const [shouldPollForSync, setShouldPollForSync] = React.useState<boolean>(false);

    const activeIntervalRefs = React.useRef<number[]>([]);

    const {
        activity,
        isLoading: isLoadingActivity,
        error: loadingError,
    } = useGetActivityForModal(props.activityId, isCreating);
    const {user, isLoading: isLoadingUser} = useGetSessionUser();
    const {teams, isLoading: isLoadingTeams} = useGetTeams();
    const {calendarSync, isLoading: isLoadingCalendarSync} = useGetCalendarSyncForActivityModal();
    const {
        isConnectedToExternalCalendar,
        isLoading: isLoadingConnectedToExternalCalendar,
        startPolling,
        stopPolling,
    } = useGetActivitySyncStatus(props.activityId, !props.activityId);

    // Use the usePoll hook to manage polling for sync status
    usePoll({
        shouldPoll: shouldPollForSync,
        startPolling,
        stopPolling,
    });

    React.useEffect(() => {
        // Stop polling when useGetActivitySyncStatus returns true
        if (isConnectedToExternalCalendar) {
            setShouldPollForSync(false);

            return;
        }

        // Do not start polling for activity sync status until we know that the
        // activity isn't already synced
        if (!activity || isLoadingConnectedToExternalCalendar) {
            return;
        }

        if (activity.isForceSync) {
            setShouldPollForSync(true);
        }
    }, [activity, isConnectedToExternalCalendar, isLoadingConnectedToExternalCalendar]);

    const {
        updateActivityForModal,
        isLoading: isUpdatingActivity,
        error: updateError,
    } = useUpdateActivityForModal();
    const {
        createActivityForModal,
        isLoading: isCreatingActivity,
        error: createError,
    } = useCreateActivityForModal();

    const {refetch: refetchTranscriptAndSummary} = useGetActivityTranscriptAndSummary(
        props.activityId,
        true
    );

    React.useEffect(() => {
        return () => {
            activeIntervalRefs.current.forEach(clearInterval);
            activeIntervalRefs.current = [];
        };
    }, []);

    const handlePollForSummaryAndTranscript = React.useCallback(() => {
        return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                refetchTranscriptAndSummary()
                    .then((result) => {
                        if (result.data && result.data.activity) {
                            // Stop polling if transcription is not enabled
                            if (result.data.activity.transcriptionStatus === 'NOT_ENABLED') {
                                // @ts-expect-error
                                removeInterval(interval);
                                // @ts-expect-error
                                resolve();

                                return;
                            }

                            if (result.data.activity && result.data.activity.summary) {
                                if (
                                    result.data.activity.summary.status === 'ERRORED' ||
                                    result.data.activity.transcriptionStatus === 'ERRORED'
                                ) {
                                    // @ts-expect-error
                                    removeInterval(interval);
                                    reject(
                                        new Error('Error generating summary, please try again.')
                                    );

                                    return;
                                }

                                if (result.data.activity.summary.summary) {
                                    // @ts-expect-error
                                    removeInterval(interval);
                                    // @ts-expect-error
                                    resolve();
                                }
                            }
                        }
                    })
                    .catch((error) => {
                        // @ts-expect-error
                        removeInterval(interval);
                        reject(error);
                    });
            }, 1500);

            // @ts-expect-error
            activeIntervalRefs.current.push(interval);
        });
    }, [refetchTranscriptAndSummary]);

    const handlePollForRecordings = React.useCallback(() => {
        return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                refetchTranscriptAndSummary()
                    .then((result) => {
                        if (
                            result.data &&
                            result.data.activity &&
                            !result.data.activity.generatingRecording
                        ) {
                            // @ts-expect-error
                            removeInterval(interval);
                            // @ts-expect-error
                            resolve();
                        }
                    })
                    .catch((error) => {
                        // @ts-expect-error
                        removeInterval(interval);
                        reject(error);
                    });
            }, 1500);

            // @ts-expect-error
            activeIntervalRefs.current.push(interval);
        });
    }, [refetchTranscriptAndSummary]);

    const removeInterval = (interval: number) => {
        clearInterval(interval);
        activeIntervalRefs.current = activeIntervalRefs.current.filter((i) => i !== interval);
    };

    const {activityDelete, isLoading: isDeletingActivity} = useDeleteActivity();

    const relatedEntityIds = getRelatedEntityIds(activity);
    const shouldSkipPotentialEntities = relatedEntityIds.length === 0;
    const {relatedEntities, participants} = useGetPotentialActivityEntities(
        relatedEntityIds,
        shouldSkipPotentialEntities
    );

    const handleUpdateActivity = (
        activityToUpdate: ActivityUpdateInput,
        location: UpdateLocation
    ): Promise<any> => {
        setLastUpdateLocation(location);

        return updateActivityForModal(activityToUpdate).then((a) => {
            if (isCreating) {
                setCreatedActivity(a.data && a.data.activityUpdate.activity);
            }
        });
    };

    const handleCreateActivity = (activityToCreate: ActivityCreateInput) => {
        createActivityForModal(activityToCreate).then((result) => {
            if (result.data && result.data.activityCreate && result.data.activityCreate.activity) {
                props.history.replace({
                    ...props.history.location,
                    hash: `/${result.data.activityCreate.activity.id}`,
                    state: {modal: true},
                });

                // Dispatch event so any component that needs to refetch on
                // activity created can listen for this (ex: ActivitiesWidget)
                window.dispatchEvent(new CustomEvent('activityCreated'));

                if (props.variant === 'log') {
                    Timeline.refreshTimeline();
                }

                if (NutClientConfig.hasLeadPageRedesignM3FF) {
                    refetchForEvent('activityStatusChanged');
                } else {
                    Peep.reloadActivities();

                    // Updates stages carousel if we completed a stage goal
                    // @ts-expect-error
                    if (window.currentLead) {
                        // @ts-expect-error
                        window.currentLead.fetch();
                    }
                }
            }
        });
    };

    const handleDeleteActivity = (input: ActivityDeleteInput) => {
        return activityDelete(input).then(() => {
            refreshActivityState(true, input.activity);
            window.dispatchEvent(new CustomEvent('activityDeleted'));

            if (NutClientConfig.hasLeadPageRedesignM3FF) {
                refetchForEvent('activityStatusChanged');
            } else {
                $(window).trigger(Lead.events.LeadBackboneModelDidUpdate);
                Peep.reloadActivities();
            }

            props.onClose();
        });
    };

    // If we're already polling for summary generation, no need to also poll for
    // recordings too
    React.useEffect(() => {
        if (activity && activity.generatingRecording && !activity.generatingSummary) {
            handlePollForRecordings();
        }
    }, [activity, handlePollForRecordings]);

    const isLoading =
        (isLoadingActivity ||
            isLoadingUser ||
            isDeletingActivity ||
            isLoadingTeams ||
            isLoadingCalendarSync) &&
        !shouldPollForSync;

    const activityNotFound = !isCreating && !isLoadingActivity && !activity;
    const notFoundError = activityNotFound ? 'Activity not found' : null;

    return (
        <ActivityModalContextProvider
            entityId={props.relatedEntityId}
            variant={variant}
            handleCreateActivity={handleCreateActivity}
            isCreatingActivity={isCreatingActivity}
            createError={createError}
            calendarSync={calendarSync}
            userId={user && user.id}
        >
            <ActivityModal
                isOpen={props.isOpen}
                onClose={props.onClose}
                activity={createdActivity || activity}
                loadingError={loadingError || notFoundError}
                updateError={updateError}
                isLoading={isLoading}
                currentUser={user}
                updateActivityForModal={handleUpdateActivity}
                isUpdatingActivity={isUpdatingActivity}
                potentialRelatedEntities={relatedEntities}
                potentialParticipants={participants}
                deleteActivity={handleDeleteActivity}
                handlePollForSummaryAndTranscript={handlePollForSummaryAndTranscript}
                teams={teams}
                lastUpdateLocation={lastUpdateLocation}
                shouldPollForSync={shouldPollForSync}
                setShouldPollForSync={setShouldPollForSync}
                variant={variant}
            />
        </ActivityModalContextProvider>
    );
}
